import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ClickAwayListener, Divider, Fade, IconButton, InputAdornment, MenuItem, MenuList, Popper, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { AuthContext } from '../../../contexts/AuthContext';
import { DashboardsContext } from '../../../contexts/DashboardsContext/DashboardsProvider';
import useBackwardsCompatible from "../../../hooks/useBackwardsCompatible";
import { getAllUsersFunc, updateDashboardFunc } from '../../../lib/helper';
import { scrollbarStyle } from '../../../shared/dashboard';
import theme from '../../../theme';
import { Dashboard, DealOwner, RefreshDataEnum } from "../../../types/files";
import { FallbackLoading } from '../../templates/loader';

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 'max-content',
        minWidth: 200,
        maxWidth: 400,
        height: 'fit-content',
        marginTop: 16,
        padding: '12px 16px',
        overflowX: 'hidden',
        overflowY: 'auto',
        borderRadius: 12,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
        zIndex: 1500,
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['900'],
    },
    label: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    mainlabel: {
        fontWeight: '600',
        fontSize: '1rem',
        color: theme.colors.neutral['600'],
    },
    sublabel: {
        maxWidth: '30ch',
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    menu: {
        width: '100%',
        padding: 4,
        "&.Mui-focusVisible": {
            background: 'transparent !important',
        },
        "&:hover": {
            background: `rgb(246, 251, 251, 1)`,
        }
    },
    icon: {
        color: theme.colors.primary['500'],
        width: 24,
        height: 24,
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 40,
            padding: '0 8px !important',
            borderRadius: 8,
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
}));

const SelectDealOwnerModalNew: React.FC<{
    open: boolean,
    dashboard: Dashboard,
    anchorElement?: HTMLElement|null,
    onClose: () => void,
}> = ({ open, dashboard, anchorElement, onClose }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { setDashboards } = useContext(DashboardsContext);
    const { dashboardOwner } = useBackwardsCompatible();

    const [loading, setLoading] = useState<boolean>(false);
    const [userId, setUserId] = useState<string>('');
    const [options, setOptions] = useState<DealOwner[]>([]);
    const [owners, setOwners] = useState<(DealOwner | null)[]>([]);
    const userEmailRef = useRef<TextFieldProps>(null);

    const debouncedName = useDebouncedCallback((name) => {
        setUserId(name);
    }, 100, { maxWait: 200 });

    const checkSelected = useCallback((owner: DealOwner) => {
        const dealOwner = dashboardOwner(dashboard);

        return (dealOwner?.name?.toLowerCase() === owner?.name?.toLowerCase())
            && (dealOwner?.email?.toLowerCase() === owner?.email?.toLowerCase());
    }, [dashboard, dashboardOwner]);

    const handleSave = useCallback(async (selection: DealOwner|null) => {
        if (!dashboard)
            return;

        setLoading(true);

        const ownerName = selection?.name?.trim() || '';
        const ownerEmail = selection?.email?.trim() || '';
        const updatedDashboard = {
            ...dashboard,
            dealOwner: {name: ownerName, email: ownerEmail},
            userId: ownerEmail ?? dashboard.dealOwner?.email,
            refreshData: {
                ...dashboard.refreshData,
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Title,
            },
        };

        updateDashboardFunc(updatedDashboard).then(() => {
            setDashboards((prev) =>
                prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
            );
            setLoading(false);
        }).finally(onClose);
    // eslint-disable-next-line
    }, [dashboard]);

    const handleClearSearch = useCallback(() => {
        if (userEmailRef.current)
            userEmailRef.current.value = '';
        setUserId('');
    }, []);

    useEffect(()=>{
        if (userGroup) {
            getAllUsersFunc(userGroup).then(users => setOptions(users.map((user: any) => ({
                name: `${user.firstName} ${user.lastName}`,
                email: user.email,
            }) as DealOwner)));
        }
    }, [userGroup]);

    useEffect(() => {
        if (!!options.length) {
            setOwners(options.filter((user) => !userId
                || user?.name?.toLowerCase().includes(userId?.toLowerCase())
                || user?.email?.toLowerCase().includes(userId?.toLowerCase())
            ));
        }
    }, [options, userId]);

    return (<>
        <Popper open={open} anchorEl={anchorElement} placement="bottom-start" sx={{ zIndex: 5000 }}>
            <ClickAwayListener onClickAway={onClose}>
                <Stack className={classes.card} alignItems="flex-start" justifyContent="flex-start" width="100%">
                    <TextField autoFocus
                        variant="outlined"
                        className={classes.inputField}
                        placeholder={'Search deal owner ...'}
                        onChange={(e) => debouncedName(e.target.value)}
                        inputRef={userEmailRef}
                        sx={{ width: '100%' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" >
                                    {!!userId && (
                                        <Fade in timeout={1000}>
                                            <IconButton size="small"
                                                onClick={handleClearSearch}
                                                sx={{ color: theme.palette.warning.dark }}
                                                disableRipple>
                                                <CancelIcon sx={{ width: 24, height: 24, }} />
                                            </IconButton>
                                        </Fade>
                                    )}
                                </InputAdornment>
                    )}} />
                    <MenuList sx={{ minWidth: 300 }}>
                        {loading ? (<FallbackLoading height="200px" />) : owners.length ? (
                            <Stack direction="column" sx={{ ...scrollbarStyle, maxHeight: 256, overflowY: 'auto' }} >
                                {owners.map((owner, i) => (<>
                                    <MenuItem className={classes.menu}
                                        onClick={() => !checkSelected(owner!) ? handleSave(owner!) : undefined }
                                        key={'owner-filter-271-' + i}>
                                        <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="flex-start" width="100%">
                                            <Stack alignItems="center" justifyContent="center" p={2} width={32} height={32}
                                                sx={{ borderRadius: 4, background: 'linear-gradient(90deg, #2dd4bf 0%, #0d9488 100%)' }}>
                                                <Typography fontSize="0.75rem" fontWeight={600} fontFamily="Lato" color="#fff">
                                                    {owner?.name ? owner.name.split(' ').slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('') : ''}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between" width="100%">
                                                <Stack direction="column" alignItems="flex-start" justifyContent="center">
                                                {!!owner?.name && (
                                                    <Typography  fontFamily="Lato"
                                                        fontWeight={400}
                                                        fontSize="0.9rem"
                                                        color="#334155">
                                                        {owner?.name}
                                                    </Typography>
                                                )}
                                                </Stack>
                                                {checkSelected(owner!) && (
                                                    <Fade in timeout={500}>
                                                        <CheckCircleIcon className={classes.icon} />
                                                    </Fade>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </MenuItem>
                                    <Divider sx={{ width: '100%' }} />
                                </>))}
                            </Stack>
                        ) : !!userId && (
                            <Stack alignItems="center" justifyContent="center" width="100%" height={200}>
                                <Typography fontSize="1rem" color="#7e7e7e">
                                    {'No results found.'}
                                </Typography>
                            </Stack>
                        )}
                    </MenuList>
                </Stack>
            </ClickAwayListener>
        </Popper>
    </>);
}

export default SelectDealOwnerModalNew;